import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { AccountRefSchema } from '../../users/account-ref/account-ref';
import { AppealResolutionSchema } from '../appeal-resolution/appeal-resolution';
import { ViolationActionSchema } from '../violation-action/violation-action';
import { ViolationBehaviorHistorySchema } from '../violation-behavior-history/violation-behavior-history';
import { ViolationBehaviorRuleSchema } from '../violation-behavior-rule/violation-behavior-rule';
import { ViolationSeveritySchema } from '../violation-severity/violation-severity';
import { ViolationStatusSchema } from '../violation-status/violation-status';
import { ViolationTypeKeySchema } from '../violation-type/violation-type';

/**
 * Why a detection is waiting for a human instead of having been acted on.
 *
 * Recorded rather than inferred, because the three routes into
 * `queued_for_review` need **different** things from the reviewer and the
 * distinction is invisible afterwards otherwise. `below_threshold` means the
 * classifier was unsure and its score is on the row; `ai_unavailable` means
 * there is no score at all and the reviewer is the *first* judgement, not the
 * second; `type_requires_review` means an operator asked for a human on this
 * category however sure the machine was.
 *
 * It is also the operational signal that matters most: a queue filling with
 * `ai_unavailable` is an outage or an exhausted budget, not a spike in abuse.
 *
 * **A behavioural flag (P27) adds no fourth reason**, deliberately. It reaches a
 * human because its seeded type carries `review_before_enforce` — which is what
 * `type_requires_review` already means. A reason per *detector* would encode a
 * distinction the type configuration makes better, and would go stale the day an
 * operator retunes that type.
 */
export const VIOLATION_QUEUE_REASONS = [
  'below_threshold',
  'ai_unavailable',
  'type_requires_review',
] as const;

export const ViolationQueueReasonSchema = z.enum(VIOLATION_QUEUE_REASONS);

export type ViolationQueueReason = (typeof VIOLATION_QUEUE_REASONS)[number];

/**
 * The message a violation is about — ids only, because this rides the list.
 */
export const ViolationMessageSubjectSchema = z
  .object({
    kind: z.literal('message'),
    messageId: UuidSchema,
    conversationId: UuidSchema,
  })
  .strict();

/**
 * The counted pattern a behavioural violation is about (P27).
 *
 * Everything a reviewer needs is here — what was counted, over how long, and
 * against which configured threshold — because unlike a message there is no
 * other place the evidence could be fetched from later. The acts themselves
 * (likes, matches, messages) may be gone by review time; the row must still
 * make the case.
 *
 * `observedCount >= threshold` is checked rather than assumed: a flag exists
 * *because* the count reached the threshold, and a row that says otherwise
 * contradicts its own reason for existing.
 */
export const ViolationBehaviorSubjectSchema = z
  .object({
    kind: z.literal('behavior'),
    rule: ViolationBehaviorRuleSchema,
    /** How many acts were counted in the window. */
    observedCount: z.number().int().min(0),
    /** The configured threshold **as it was when the flag was raised**. */
    threshold: z.number().int().min(1),
    windowSeconds: z.number().int().min(1),
    windowStartedAt: z.iso.datetime(),
  })
  .strict()
  .refine((subject) => subject.observedCount >= subject.threshold, {
    error: 'observedCount must have reached threshold',
    path: ['observedCount'],
  });

/** What a violation is about, on the list. */
export const ViolationSubjectSchema = z.discriminatedUnion('kind', [
  ViolationMessageSubjectSchema,
  ViolationBehaviorSubjectSchema,
]);

export type ViolationSubject = z.infer<typeof ViolationSubjectSchema>;

/**
 * One account judged against one violation type — the row the queue works and
 * the record N20 requires every enforcement to leave.
 *
 * **The evidence is NOT here**, and the omission is deliberate in the same way
 * `ReportSchema`'s is: this is the *list* shape, and a list response is what
 * gets paged, cached and exported. A message's body travels on
 * {@link ViolationDetailSchema}, which a reviewer fetches one row at a time —
 * and unlike a report, that detail *must* carry it, because there is no other
 * surface in the product through which a moderator can read a member's
 * conversation.
 *
 * **`subject` is a discriminated union, not a bag of nullable fields** (P23c2a:
 * fields that are null together are one nullable object). Before P27 this shape
 * had `messageId` and `conversationId` at the top level, which is precisely what
 * made a behavioural flag — an account and a counted pattern, with no message —
 * unsayable without a second table and a second enforcement path.
 *
 * The type's `key`, `severity` and `action` are denormalized onto the row for
 * the reason `Report.openReportsAgainstMember` is: the queue would otherwise be
 * an N+1 across the page (P14b). They are the type's values **as read now**, not
 * as they were at detection — `actionTaken` is the historical fact, and the two
 * differ exactly when an operator has retuned the type since, which is a
 * difference a reviewer needs to see rather than have hidden.
 */
export const ViolationSchema = z
  .object({
    id: UuidSchema,
    subject: ViolationSubjectSchema,
    /** Whose account this is against. The one fact every subject kind has. */
    offenderUserId: UuidSchema,
    /**
     * Which rule was broken — **null when nobody has said yet**.
     *
     * A message the platform could not classify at all (an AI refusal, an
     * exhausted budget) is queued with no type, because N15 fails closed and
     * there is genuinely nothing to name: inventing a placeholder category would
     * put a fiction in the record a member appeals against. The database refuses
     * to let such a row be *enforced* until a human supplies the classification
     * (`violations_type_required_to_enforce`).
     *
     * The four type fields are therefore nullable **together** — they are one
     * fact — and a client renders "not yet classified" rather than a blank rule.
     */
    violationTypeId: UuidSchema.nullable(),
    violationTypeKey: ViolationTypeKeySchema.nullable(),
    /** The type's current values — see the note above on why "current". */
    violationTypeName: z.string().nullable(),
    severity: ViolationSeveritySchema.nullable(),
    configuredAction: ViolationActionSchema.nullable(),
    /**
     * What the classifier scored this label at, 0–1.
     *
     * **Null when there is no score**, which is not the same as a score of zero:
     * an AI refusal or an exhausted budget queues the message with nothing to
     * report, and a `0` there would read as "the model was certain it was fine"
     * — the precise misreading that would let a reviewer wave it through. A
     * behavioural flag never has one: it counted, it did not judge.
     */
    aiConfidence: z.number().min(0).max(1).nullable(),
    /** The model's own rationale, when one was asked for. Staff-facing. */
    aiReason: z.string().nullable(),
    status: ViolationStatusSchema,
    /** Present once the row leaves `detected`; null while it is still in flight. */
    queueReason: ViolationQueueReasonSchema.nullable(),
    /**
     * The action that actually ran — the historical fact, never re-derived from
     * the type. A type edited after the event must not rewrite what was done to
     * somebody.
     */
    actionTaken: ViolationActionSchema.nullable(),
    enforcedAt: z.iso.datetime().nullable(),
    /** When a `suspend` action's suspension ends. Null for every other action. */
    suspensionUntil: z.iso.datetime().nullable(),
    detectedAt: z.iso.datetime(),
    appealedAt: z.iso.datetime().nullable(),
    /** The member's own words. Staff read it; it is never shown to anyone else. */
    appealText: z.string().nullable(),
    appealResolution: AppealResolutionSchema.nullable(),
    /** The moderator's note on the appeal — staff-facing. */
    reviewNote: z.string().nullable(),
    reviewedAt: z.iso.datetime().nullable(),
    reviewedByUserId: UuidSchema.nullable(),
    /**
     * Whose account this is — resolved server-side, one batched lookup per page.
     *
     * On the LIST and not only the detail, because a queue whose rows are uuids
     * is a queue nobody can work, and the fix cannot be a client lookup:
     * `GET /admin/users` answers to `members.read`, which the seeded `moderator`
     * role does not hold, so resolving names in the browser would show them to an
     * `admin` and uuids to the reviewer this queue exists for (P19b).
     *
     * Nullable although `offender_user_id` is `notNull` — a `null` can only mean
     * the batched lookup came back short, and the row is still a case somebody
     * must decide.
     */
    offender: AccountRefSchema.nullable(),
  })
  .strict();

export type Violation = z.infer<typeof ViolationSchema>;

/**
 * A message subject as a reviewer opens it — the ids, plus the message itself.
 *
 * **`body` is the reason the detail shape exists.** A moderator cannot decide
 * whether a message broke a rule without reading it, and the product has no
 * other surface that would show it to them — chat is member-to-member by
 * construction (N13) and no admin conversation viewer exists. Putting it here
 * rather than on the list row confines member content to a single-row read under
 * `violations.review`, which is the narrowest place it can live while the
 * decision remains possible at all.
 */
export const ViolationMessageEvidenceSchema = ViolationMessageSubjectSchema.extend({
  body: z.string(),
  /** What moderation has since done to the message itself. */
  hidden: z.boolean(),
}).strict();

/**
 * A behavioural subject as a reviewer opens it — the counted pattern, plus the
 * member's history against every rule.
 *
 * The evidence itself is already on the list row (the count, the threshold, the
 * window — there is no member content to fetch under a narrower permission).
 * What the detail adds is what a single flag cannot show: whether this is a
 * first slip or a pattern. `history` is computed in the detail's own read, one
 * grouped statement, so the queue never pays for it per row (P27a3).
 *
 * `safeExtend`, not `extend`: the list arm refines `observedCount >= threshold`,
 * and `extend` would drop that check silently.
 */
export const ViolationBehaviorEvidenceSchema = ViolationBehaviorSubjectSchema.safeExtend({
  history: ViolationBehaviorHistorySchema,
});

/** What a violation is about, as a reviewer opens it. */
export const ViolationSubjectDetailSchema = z.discriminatedUnion('kind', [
  ViolationMessageEvidenceSchema,
  ViolationBehaviorEvidenceSchema,
]);

export type ViolationSubjectDetail = z.infer<typeof ViolationSubjectDetailSchema>;

/**
 * A violation as a reviewer opens it: the row, the evidence, and the account.
 *
 * `offender` is **inherited**, not re-declared: it moved onto the list row when
 * the queue needed it, and two shapes for one fact drift.
 */
export const ViolationDetailSchema = ViolationSchema.omit({ subject: true })
  .extend({
    subject: ViolationSubjectDetailSchema,
    /**
     * How many violations already count against this member
     * (`countsAgainstMember`), this one included.
     *
     * The number that separates a first mistake from a pattern, computed in the
     * detail's own statement rather than fetched per row.
     */
    standingViolations: z.number().int().min(0),
  })
  .strict();

export type ViolationDetail = z.infer<typeof ViolationDetailSchema>;
